import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import BlogManagement from '../components/BlogManagement';
import TutorialModal from '../components/TutorialModal';
import { hasSeenTutorial } from '../utils/tutorialData';
import { isLeader } from '../utils/permissions';
import {
    BlogIcon as FileText,
    CheckCircleIcon as CheckCircle,
    TimeIcon as Clock,
    DeleteIcon as Trash,
    PlusIcon as Plus,
    EditIcon as Edit2,
} from '../components/Icons';

const StatusBadge = ({ status }) => {
    const config = {
        draft: { label: 'Entwurf', className: 'bg-surface-onVariant/10 text-surface-onVariant' },
        pending: { label: 'Eingereicht', className: 'bg-tertiary-container text-tertiary-onContainer' },
        published: { label: 'Veröffentlicht', className: 'bg-primary-container text-primary-onContainer' },
    };
    const c = config[status] || config.draft;
    return (
        <span className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full ${c.className}`}>
            {c.label}
        </span>
    );
};

const AdminBlog = () => {
    const { profile, loadingProfile } = useOutletContext();
    const { user, API_URL } = useContext(AuthContext);
    const navigate = useNavigate();
    const [showTutorial, setShowTutorial] = useState(!hasSeenTutorial('admin-blog'));
    const [myDrafts, setMyDrafts] = useState([]);
    const [loadingDrafts, setLoadingDrafts] = useState(true);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const userIsLeader = isLeader(profile);

    const fetchMyDrafts = useCallback(async () => {
        if (!user?.token) return;
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const { data } = await axios.get(`${API_URL}/blog/my-drafts`, config);
            setMyDrafts(data);
        } catch (err) {
            console.error('Fehler beim Laden der Entwürfe:', err);
        } finally {
            setLoadingDrafts(false);
        }
    }, [user, API_URL]);

    useEffect(() => {
        fetchMyDrafts();
    }, [fetchMyDrafts]);

    const handleSubmitForReview = async (postId) => {
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.put(`${API_URL}/blog/${postId}/submit`, {}, config);
            fetchMyDrafts();
        } catch (err) {
            console.error('Fehler beim Einreichen:', err);
        }
    };

    const handleDeleteDraft = async (postId) => {
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.delete(`${API_URL}/blog/${postId}`, config);
            setDeleteConfirm(null);
            fetchMyDrafts();
        } catch (err) {
            console.error('Fehler beim Löschen:', err);
        }
    };

    const handleBlogPostCreated = (newPost) => {
        console.log('Neuer Blogbeitrag erstellt:', newPost);
        fetchMyDrafts();
    };

    if (loadingProfile) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    // Access: GL, Admin, and Kind
    if (!profile || (profile.role !== 'gruppenleiter' && profile.role !== 'admin' && profile.role !== 'kind')) {
        return (
            <div className="md3-card-elevated p-8 bg-error-container text-error-on-container">
                <h2 className="text-headline-small font-bold mb-2">Zugriff verweigert</h2>
                <p>Sie haben keinen Zugriff auf die Blog-Verwaltung.</p>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 max-w-7xl animate-fade-in">
            {showTutorial && (
                <TutorialModal pageId="admin-blog" onClose={() => setShowTutorial(false)} />
            )}

            <h1 className="text-display-small font-bold mb-8 text-surface-on">
                {userIsLeader ? 'Blog Verwaltung' : 'Blog schreiben'}
            </h1>

            {/* Create Blog Post Button */}
            <button
                onClick={() => navigate('/blog/new')}
                className="w-full md3-card-elevated p-8 mb-8 flex items-center justify-center gap-4 cursor-pointer hover:shadow-elevation-2 transition-all group border-2 border-dashed border-outlineVariant hover:border-primary/40"
            >
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform edit-pen-glow">
                    <Edit2 sx={{ fontSize: 28 }} />
                </div>
                <div className="text-left">
                    <h3 className="text-title-large font-bold text-surface-on">
                        {userIsLeader ? 'Neuen Blogbeitrag erstellen' : 'Neuen Entwurf erstellen'}
                    </h3>
                    <p className="text-body-medium text-surface-onVariant">
                        Im Editor mit Live-Vorschau schreiben
                    </p>
                </div>
            </button>

            {/* My Drafts — for Kinder (and also useful for GL/Admin) */}
            {!userIsLeader && (
                <section className="md3-card-elevated p-8 mb-8">
                    <h3 className="text-title-large font-bold text-surface-on mb-6 pb-2 border-b border-outlineVariant flex items-center gap-3">
                        <FileText style={{ fontSize: 22 }} className="text-primary" />
                        Meine Entwürfe
                    </h3>

                    {loadingDrafts ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    ) : myDrafts.length === 0 ? (
                        <div className="text-center py-12">
                            <FileText style={{ fontSize: 48 }} className="text-surface-onVariant/30 mx-auto mb-4" />
                            <p className="text-body-large text-surface-onVariant">
                                Du hast noch keine Entwürfe. Erstelle deinen ersten Blogbeitrag!
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {myDrafts.map(draft => (
                                <div key={draft._id} className="flex items-center justify-between p-4 bg-surface-containerLow rounded-2xl border border-outlineVariant/30 hover:border-outlineVariant/60 transition-colors">
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-title-medium font-bold text-surface-on truncate">{draft.title}</h4>
                                        <div className="flex items-center gap-3 mt-1">
                                            <StatusBadge status={draft.status} />
                                            <span className="text-label-small text-surface-onVariant">
                                                {new Date(draft.updatedAt).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 ml-4 shrink-0">
                                        {draft.status === 'draft' && (
                                            <button
                                                onClick={() => handleSubmitForReview(draft._id)}
                                                className="md3-btn-tonal text-sm gap-1"
                                            >
                                                <CheckCircle style={{ fontSize: 16 }} />
                                                Einreichen
                                            </button>
                                        )}
                                        {draft.status === 'pending' && (
                                            <span className="flex items-center gap-1 text-label-small text-tertiary font-medium">
                                                <Clock style={{ fontSize: 14 }} />
                                                Wartet auf Freigabe
                                            </span>
                                        )}
                                        {draft.status === 'draft' && (
                                            deleteConfirm === draft._id ? (
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleDeleteDraft(draft._id)}
                                                        className="md3-btn-filled !bg-error !text-error-on text-sm"
                                                    >
                                                        Löschen
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirm(null)}
                                                        className="md3-btn-text text-sm"
                                                    >
                                                        Abbrechen
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => setDeleteConfirm(draft._id)}
                                                    className="md3-icon-btn text-error/60 hover:text-error"
                                                >
                                                    <Trash style={{ fontSize: 18 }} />
                                                </button>
                                            )
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            )}

            {/* Blog Management — GL/Admin only */}
            {userIsLeader && (
                <section className="md3-card-elevated p-8 mb-8">
                    <h3 className="text-title-large font-bold text-surface-on mb-6 pb-2 border-b border-outlineVariant">
                        Blogbeiträge verwalten
                    </h3>
                    <BlogManagement />
                </section>
            )}
        </div>
    );
};

export default AdminBlog;

